import { useCallback, useEffect, useRef, useState } from "react";
import type { CommuteRequest, CommuteSnapshot } from "../shared/types";
import { fetchSnapshot } from "./api";

/**
 * Refresh policy.
 *
 * 15s in the foreground: fast enough that a departure countdown never drifts by a visible
 * amount, slow enough to be free. Driving time is refreshed by the same request, but the
 * Worker caches route lookups, so polling here does not translate into Google calls. A hidden
 * tab polls nothing at all.
 */
const FOREGROUND_INTERVAL_MS = 15_000;

export interface SnapshotState {
	snapshot: CommuteSnapshot | null;
	error: string | null;
	loading: boolean;
	fetchedAtMs: number | null;
	offline: boolean;
	refresh: () => void;
}

export function useSnapshot(request: CommuteRequest, enabled = true): SnapshotState {
	const [snapshot, setSnapshot] = useState<CommuteSnapshot | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [fetchedAtMs, setFetchedAtMs] = useState<number | null>(null);
	const [offline, setOffline] = useState(!navigator.onLine);

	const abortRef = useRef<AbortController | null>(null);
	// The request object is rebuilt on every render; comparing its serialisation is what keeps
	// the effect from re-subscribing on each keystroke in the settings sheet.
	const requestKey = JSON.stringify(request);
	const requestRef = useRef(request);
	requestRef.current = request;

	const load = useCallback(async () => {
		abortRef.current?.abort();
		const controller = new AbortController();
		abortRef.current = controller;
		setLoading(true);
		try {
			const next = await fetchSnapshot(requestRef.current, controller.signal);
			setSnapshot(next);
			setFetchedAtMs(Date.now());
			setError(null);
			setOffline(false);
		} catch (cause) {
			if (controller.signal.aborted) {
				return;
			}
			setError(cause instanceof Error ? cause.message : "Could not reach GoTime.");
			if (!navigator.onLine || cause instanceof TypeError) {
				setOffline(true);
			}
		} finally {
			if (!controller.signal.aborted) {
				setLoading(false);
			}
		}
	}, []);

	useEffect(() => {
		if (!enabled) {
			return;
		}
		void load();
	}, [load, requestKey, enabled]);

	useEffect(() => {
		if (!enabled) {
			return;
		}
		let timer: number | undefined;

		const schedule = () => {
			window.clearInterval(timer);
			if (document.visibilityState === "visible") {
				timer = window.setInterval(() => void load(), FOREGROUND_INTERVAL_MS);
			}
		};

		const onVisibility = () => {
			if (document.visibilityState === "visible") {
				void load();
			}
			schedule();
		};

		const onOnline = () => {
			setOffline(false);
			void load();
		};
		const onOffline = () => setOffline(true);

		schedule();
		document.addEventListener("visibilitychange", onVisibility);
		window.addEventListener("online", onOnline);
		window.addEventListener("offline", onOffline);

		return () => {
			window.clearInterval(timer);
			document.removeEventListener("visibilitychange", onVisibility);
			window.removeEventListener("online", onOnline);
			window.removeEventListener("offline", onOffline);
		};
	}, [load, enabled]);

	useEffect(() => () => abortRef.current?.abort(), []);

	return { snapshot, error, loading, fetchedAtMs, offline, refresh: () => void load() };
}

/** A ticking clock for countdowns, so the page does not depend on the polling interval. */
export function useNow(intervalMs = 1000): number {
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
		return () => window.clearInterval(timer);
	}, [intervalMs]);
	return now;
}
